import { styled } from "styled-components";
import { useLayoutEffect, useRef } from "react";

import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import {
  ISmartSearchUsageResponse,
  Loading,
  QUERIES,
  formatDate,
  getSmartSearchUsage,
  isEmpty,
} from "@dataone/lib";
import { useQuery } from "@tanstack/react-query";
import { NoDataIcon } from "@dataone/icons";
import dayjs from "dayjs";

interface ChartProfileProps {
  widthChart: string;
  heightChart: string;
}

const ChartProfile = ({ widthChart, heightChart }: ChartProfileProps) => {
  const chartRef = useRef<am5xy.XYChart | null>(null);

  const smartSearchUsageFilter = {
    startDate: dayjs().subtract(30, "days").format("YYYY-MM-DD"),
    endDate: dayjs().format("YYYY-MM-DD"),
  };

  const { data, isLoading } = useQuery({
    queryKey: [QUERIES.SEARCH_USAGE, smartSearchUsageFilter],
    queryFn: () => getSmartSearchUsage(smartSearchUsageFilter!),
  });

  const generateDateRange = () => {
    const dateRange = [];
    const startDate = new Date(smartSearchUsageFilter.startDate);
    const endDate = new Date(smartSearchUsageFilter.endDate);
    const newEndDate = new Date(endDate.setDate(endDate.getDate() + 1));

    for (
      let date = startDate;
      date <= newEndDate;
      date.setDate(date.getDate() + 1)
    ) {
      dateRange.push(formatDate(date));
    }
    return dateRange;
  };

  const mapDateRangeToUsage = (data: ISmartSearchUsageResponse[]) =>
    generateDateRange().map((date) => ({
      ...{
        date,
        searchUsage: 0,
        chatUsage: 0,
        promptGridItemUpdateUsage: 0,
      },
      ...(data.find((item) => item.date === date) || {}),
    }));

  useLayoutEffect(() => {
    if (isEmpty(data)) return;
    let root = am5.Root.new("profilechart", {
      calculateSize: function (dimensions) {
        return {
          width: dimensions.width,
          height: dimensions.height,
        };
      },
    });

    let gradient1 = am5.color("#16CCBF");

    let gradient2 = am5.color("#638AFF");

    let gradient3 = am5.color("#FFB800");

    root?._logo?.dispose();

    root.setThemes([am5themes_Animated.new(root)]);

    let chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panY: false,
        layout: root.verticalLayout,
      })
    );

    let yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, {}),
      })
    );

    let xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        renderer: am5xy.AxisRendererX.new(root, {}),
        categoryField: "date",
        tooltip: am5.Tooltip.new(root, {}),
      })
    );

    let formattedData = mapDateRangeToUsage(data ?? []);

    xAxis.data.setAll(formattedData);

    let series1 = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: "AI Search",
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: "searchUsage",
        categoryXField: "date",
        fill: gradient2,
        tooltip: am5.Tooltip.new(root, {
          labelText: "{valueY}",
        }),
      })
    );

    series1.columns.template.setAll({
      cornerRadiusTL: 5,
      cornerRadiusTR: 5,
      // width: 20,
    });
    series1.data.setAll(formattedData);

    let series2 = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: "Ai Consultancy",
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: "chatUsage",
        categoryXField: "date",
        fill: gradient1,
        tooltip: am5.Tooltip.new(root, {
          labelText: "{valueY}",
        }),
      })
    );

    series2.columns.template.setAll({
      cornerRadiusTL: 5,
      cornerRadiusTR: 5,
      // width: 20,
    });

    series2.data.setAll(formattedData);

    let series3 = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: "Prompt Update",
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: "promptGridItemUpdateUsage",
        categoryXField: "date",
        fill: gradient3,
        tooltip: am5.Tooltip.new(root, {
          labelText: "{valueY}",
        }),
      })
    );

    series3.columns.template.setAll({
      cornerRadiusTL: 5,
      cornerRadiusTR: 5,
      // width: 20,
    });

    series3.data.setAll(formattedData);

    let legend = chart.children.push(am5.Legend.new(root, {}));
    legend.data.setAll(chart.series.values);

    let cursor = chart.set("cursor", am5xy.XYCursor.new(root, {}));
    cursor.lineY.set("visible", false);

    chartRef.current = chart;

    return () => {
      root.dispose();
    };
  }, [data]);

  return isLoading ? (
    <Loading />
  ) : data && data?.length > 0 ? (
    <StyledChartProfile
      id="profilechart"
      style={{ width: widthChart, height: heightChart }}
    ></StyledChartProfile>
  ) : (
    <StyledEmptyChart>
      <div className="no-data">
        <NoDataIcon />
      </div>
      <div className="message">Data not found</div>
      <p>Start searching and you'll be able to visualize your daily usage.</p>
    </StyledEmptyChart>
  );
};

export default ChartProfile;

const StyledChartProfile = styled.div`
  font-size: 14px;
  margin: 0 auto;
`;

const StyledEmptyChart = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 25px 50px;
  .message {
    font-size: 18px;
    font-weight: 700;
  }
  p {
    font-size: 14px;
  }
  .no-data {
    font-size: 110px;
  }
`;
